import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  Box,
  Receipt,
  CalendarClock,
  UserRound,
  Users,
  ClipboardList,
  Inbox,
  LayoutDashboard,
  Settings,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { countPending, countPendingFieldEvents, getOutboxDb } from '../lib/outbox.js';
import { BottomTabNav } from '../components/bottom-tab-nav.js';
import { BrandGlyph } from '../components/brand-glyph.js';
import { useAuth } from '../lib/auth-store.js';
import { cn } from '../lib/cn.js';

/**
 * Root app layout — three responsive zones:
 *
 *   - Mobile: compact top header (logo + outbox chip) + bottom tab nav
 *     (Design/hvac_mobile_inventory_quote_signature.html pattern).
 *   - md+ : persistent left rail with full route list; main column scrolls.
 *
 * The mockups treat the bottom tabs as the canonical mobile chrome — so the
 * top header is intentionally lean on mobile.
 */
export function RootLayout() {
  // Badge counts BOTH queues: sales-order submissions and field captures
  // (parts/photos/notes/signatures). Before this a tech with six queued
  // photos saw a zero badge and an "empty" outbox (audit P0-9).
  const pending = useLiveQuery(
    async () => {
      const db = getOutboxDb();
      const [orders, fieldEvents] = await Promise.all([
        countPending(db),
        countPendingFieldEvents(db),
      ]);
      return orders + fieldEvents;
    },
    [],
    0,
  );
  const path = useRouterState({ select: (s) => s.location.pathname });

  // The login route owns its own chrome — no side rail, no bottom tabs,
  // no auth-dependent affordances. Keeps the layout free of UX that
  // assumes a logged-in user.
  if (path === '/login') {
    return (
      <div className="min-h-full bg-surface-muted">
        <main className="px-4 py-6">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-surface-muted md:flex">
      <SideRail pending={pending} />
      <div className="flex-1 flex flex-col min-w-0">
        <MobileHeader pending={pending} />
        <main className="flex-1 pb-20 md:pb-6">
          {/* The dispatch board is a timeline — it gets the full window;
             reading-width pages keep the centred column. */}
          <div className={cn('mx-auto w-full px-4 py-4 md:py-6', isWidePage(path) ? 'max-w-none' : 'max-w-4xl')}>
            <Outlet />
          </div>
        </main>
        <BottomTabNav />
      </div>
    </div>
  );
}

/** Routes whose content is a wide grid rather than a reading column. */
function isWidePage(path: string): boolean {
  return path === '/operations' || path === '/dispatch';
}

function MobileHeader({ pending }: { pending: number }) {
  return (
    <header
      className="md:hidden sticky top-0 z-20 bg-surface border-b"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Link to="/" className="inline-flex items-center gap-2 font-semibold">
          <BrandGlyph />
          <span className="text-base tracking-tight text-ink">Tinsmith</span>
        </Link>
        <Link
          to="/outbox"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted"
          aria-label={pending > 0 ? `Outbox: ${pending} pending` : 'Outbox'}
        >
          <Inbox className="h-4 w-4" aria-hidden />
          {pending > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-amber-300 px-1 text-[10px] font-semibold text-amber-950">
              {pending}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}

interface RailItem {
  to: string;
  label: string;
  matchPrefix: string;
  Icon: LucideIcon;
  /** Optional pill — currently only Outbox uses it. */
  badge?: number;
  /** Roles that see this rail item. Undefined = visible to everyone. */
  roles?: ReadonlyArray<'admin' | 'dispatcher' | 'office' | 'field_tech'>;
}

type RailRole = 'admin' | 'dispatcher' | 'office' | 'field_tech';
const RAIL_OFFICE_ROLES: ReadonlyArray<RailRole> = ['admin', 'dispatcher', 'office'];
const RAIL_ADMIN_ONLY: ReadonlyArray<RailRole> = ['admin'];

function SideRail({ pending }: { pending: number }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const role = useAuth((s) => s.user?.role ?? null);

  const items: RailItem[] = [
    { to: '/', label: 'Today', matchPrefix: '/', Icon: LayoutDashboard, roles: RAIL_OFFICE_ROLES },
    { to: '/inventory', label: 'Inventory', matchPrefix: '/inventory', Icon: Box },
    { to: '/quote-builder', label: 'New quote', matchPrefix: '/quote-builder', Icon: Receipt },
    { to: '/jobs', label: 'Jobs', matchPrefix: '/jobs', Icon: CalendarClock },
    { to: '/customers', label: 'Customers', matchPrefix: '/customers', Icon: Users, roles: RAIL_OFFICE_ROLES },
    { to: '/operations', label: 'Operations', matchPrefix: '/operations', Icon: ClipboardList, roles: RAIL_OFFICE_ROLES },
    { to: '/trucks', label: 'Trucks', matchPrefix: '/trucks', Icon: Truck, roles: RAIL_OFFICE_ROLES },
    // /contracts route stays registered (Phase 6 stub) but hidden from
    // navigation until the service-contracts surface actually ships.
    { to: '/outbox', label: 'Outbox', matchPrefix: '/outbox', Icon: Inbox, badge: pending },
    { to: '/admin', label: 'Admin', matchPrefix: '/admin', Icon: Settings, roles: RAIL_ADMIN_ONLY },
    { to: '/me', label: 'Me', matchPrefix: '/me', Icon: UserRound },
  ].filter((item) => !item.roles || (role !== null && item.roles.includes(role)));

  return (
    <aside
      className="hidden md:flex md:w-60 md:flex-col md:sticky md:top-0 md:h-screen border-r bg-surface"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-label="Primary"
    >
      <div className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2 font-semibold">
          <BrandGlyph />
          <span className="text-lg tracking-tight text-ink">Tinsmith</span>
        </Link>
      </div>
      <nav className="flex-1 px-2 pb-4 overflow-y-auto">
        <ul className="space-y-0.5">
          {items.map(({ to, label, matchPrefix, Icon, badge }) => {
            const active =
              matchPrefix === '/'
                ? path === '/'
                : path === matchPrefix || path.startsWith(`${matchPrefix}/`);
            return (
              <li key={to}>
                <Link
                  to={to}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                    active
                      ? 'bg-brand-50 text-ink-accent font-medium'
                      : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="flex-1 truncate">{label}</span>
                  {badge !== undefined && badge > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-amber-300 px-1.5 text-[10px] font-semibold text-amber-950">
                      {badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <div
        className="px-5 py-3 text-[11px] text-ink-subtle border-t"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        Tinsmith · Field operations
      </div>
    </aside>
  );
}

